﻿import { ISessionStore } from "../session/SessionStore";
import { IFetcher } from "../../util/io/Fetcher";
import { notNullOrUndefined } from "../../util/functions/notNullOrUndefined";
import { ICurrentDocumentStore } from "../window/document/CurrentDocumentStore";
import { ICurrentWindowStore } from "../window/ICurrentWindowStore";
import { fireAndForget } from "../../util/async/fireAndForget";
import { getFromCookie } from "../../util/cookies/cookie";

export interface IPow3SessionTracker {
	init(): void;
}

interface ICheckSessionResponse {
	tokenGuid?: string;
	tokenChanged?: boolean;
}

/**
 * Performs /POW3/Account/SessionStillAlive every 45 seconds to support user setting MaxTimeBetweenPageLoads
 * Performs /POW3/Account/CheckSession every 15 seconds to support user setting SessionTokenTTL, and adds automatic logout
 * once FormsAuthentication session is expired
 */
export class Pow3SessionTracker implements IPow3SessionTracker {
	private readonly checkSessionIntervalMs: number = 15000 as const;
	private readonly sessionStillAliveIntervalMs: number = 45000 as const;

	constructor(
		private readonly sessionStore: ISessionStore,
		private readonly currentDocumentStore: ICurrentDocumentStore,
		private readonly currentWindowStore: ICurrentWindowStore,
		private readonly fetcher: IFetcher,
	) {}

	private checkSessionResponse: ICheckSessionResponse | null = null;
	private mouseTracker: MouseTracker | null = null;

	public init() {
		const { sessionStore } = this;
		if (sessionStore.isLoggedIn) {
			fireAndForget(this.checkSession());
			this.sessionStillAlive();
		}
	}

	private sessionStillAlive() {
		setInterval(async () => {
			if (this.mouseTracker?.hasMoved()) {
				this.mouseTracker = null;
				fireAndForget(this.fetcher.get("/POW3/Account/SessionStillAlive"));
			}
		}, this.sessionStillAliveIntervalMs);

		const { currentDocumentStore } = this;
		const { currentDocument } = currentDocumentStore;

		currentDocument.addEventListener("mousemove", (e: MouseEvent) => {
			if (this.mouseTracker === null) {
				this.mouseTracker = new MouseTracker(e.clientX, e.clientY);
			} else {
				this.mouseTracker.set(e.clientX, e.clientY);
			}
		});
	}

	private async checkSession(tokenGuid?: string): Promise<void> {
		const { fetcher } = this;

		try {
			let result: Response;

			if (notNullOrUndefined(tokenGuid)) {
				result = await fetcher.fetch(`/POW3/Account/CheckSession?tokenGuid=${tokenGuid}`, { method: "GET" });
				if (result.status === 419) {
					const checkSessionResponse = (await result.json()) as ICheckSessionResponse;
					if (checkSessionResponse.tokenChanged) {
						// POW3 session token changed, this is possible when "log in as" was used from the management pages.
						// This used to be "window.location.reload()", but was changed to redirect to the login page
						// For example, if you go to https://local.pacsonweb.com/POW3/App/Study/2/1152 and you DON'T have the rights to access that study
						// It will load the viewer and present you with a red dialog that says "Unauthorized to open study"
						// People think this is an "error", but it can happen if you use the URL based integration and just open a second tab with a second URL integration
						// The first tab will reload and show the red box, but what should actually happen is that people are sent to the login page
						this.returnToLoginAfterLogout();
					} else {
						await this.logOut();
					}
					return;
				}
				return;
			}

			result = await fetcher.fetch(`/POW3/Account/CheckSession`, { method: "GET" });
			if (result.status === 419) {
				await this.logOut();
				return;
			} else if (result.ok) {
				this.checkSessionResponse = (await result.json()) as ICheckSessionResponse;
			} else {
				console.error("❌ Failed to check POW3 session, unexpected response status:", result.status);
			}
		} catch (e) {
			console.error("❌ Failed to check POW3 session", e);
		} finally {
			setTimeout(async () => await this.checkSession(this.checkSessionResponse?.tokenGuid), this.checkSessionIntervalMs);
		}
	}

	private returnToLoginAfterLogout() {
		const { location } = this.currentWindowStore.currentWindow;
		const source = getFromCookie("Source");
		// Do NOT append a returnUrl here. The Account/Login endpoint automatically redirects to returnUrl
		// if the user is already logged in — but after a session token change the new session may not have
		// access to the current page. Passing returnUrl could therefore cause an infinite redirect loop, or
		// silently land the user on an "Unauthorized to view this" dialog in a background tab they weren't
		// looking at.
		if (source) {
			location.href = "/POW3/Account/Login/" + encodeURIComponent(source);
		} else {
			location.href = "/POW3/Account/Login";
		}
	}

	private async logOut(): Promise<void> {
		const { sessionStore } = this;

		try {
			await sessionStore.logout();
		} catch (error) {
			console.error("❌ Failed to logout", error);
		} finally {
			this.currentWindowStore.currentWindow.location.href = "/POW3/Account/LogOut";
		}
	}
}

class MouseTracker {
	private minX: number;
	private maxX: number;
	private minY: number;
	private maxY: number;

	constructor(x: number, y: number) {
		this.minX = x;
		this.minY = y;
		this.maxX = x;
		this.maxY = y;
	}

	public set(x: number, y: number) {
		this.minX = Math.min(this.minX, x);
		this.minY = Math.min(this.minY, y);
		this.maxX = Math.max(this.maxX, x);
		this.maxY = Math.max(this.maxY, y);
	}

	public hasMoved(): boolean {
		return Math.max(this.maxX - this.minX, this.maxY - this.minY) > 10;
	}
}
